import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { Share2, UserPlus, Flag } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { useScreenCleanMode } from "@/lib/mobileFloatingControls";
import { useLiveMatchMode } from "@/lib/mobile/liveMatchMode";
import { openShareSheet } from "@/components/viral/ShareSheetHost";
import { trackEvent } from "@/lib/audit";
import { setLayerActive, useLayerOffset } from "@/lib/pr30/zeroOverlapPolicy";

export function MobileShareInlineCard({ className = "" }: { className?: string }) {
  const { nation } = useSelectedNation();

  const onShare = () => {
    void trackEvent("mobile_share_dock_share_clicked", {});
    openShareSheet();
  };
  const onInvite = () => {
    void trackEvent("mobile_share_dock_invite_clicked", {});
    openShareSheet();
  };
  const onNation = () => {
    void trackEvent("mobile_share_dock_nation_clicked", { nation });
  };

  return (
    <nav aria-label="Quick share" className={`lg:hidden ${className}`}>
      <div className="grid grid-cols-3 gap-1 rounded-2xl border border-border/60 bg-background/85 p-1 shadow-lg backdrop-blur-md">
        <button
          type="button"
          onClick={onShare}
          className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl bg-primary px-2 text-[12px] font-black text-primary-foreground transition active:scale-95"
        >
          <Share2 className="h-4 w-4" />
          <span>Compartir</span>
        </button>
        <button
          type="button"
          onClick={onInvite}
          className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl bg-secondary px-2 text-[12px] font-black text-foreground transition active:scale-95"
        >
          <UserPlus className="h-4 w-4" />
          <span>Invitar</span>
        </button>
        <Link
          to={nation ? "/nation-wars" : "/onboarding"}
          onClick={onNation}
          className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl bg-accent px-2 text-[12px] font-black text-accent-foreground transition active:scale-95"
        >
          <Flag className="h-4 w-4" />
          <span>{nation ?? "Mi Nación"}</span>
        </Link>
      </div>
    </nav>
  );
}

/**
 * WORLD-CUP-MOBILE-UX-FINAL-P0 — Mobile Share Domination dock.
 *
 * Fixed bottom bar (mobile only) with three high-leverage actions:
 * Share / Invite / My Nation. Sits ABOVE BottomNav (which is ~64px tall
 * plus safe-area inset). Hidden in clean-screen mode and in live-match
 * mode (so it doesn't compete with the marker / prediction). Each button
 * is a 48px hit target — comfortably above the 44px minimum.
 */
export function MobileShareDock() {
  const isMobile = useIsMobile();
  const cleanScreen = useScreenCleanMode();
  const liveMode = useLiveMatchMode();
  const { nation } = useSelectedNation();
  // P0-MOBILE-OVERLAY-02 — the dock is the canonical floating_cta surface
  // on mobile, so it MUST self-register that layer in PR-30 Zero Overlap
  // Policy regardless of route. Otherwise lower-priority layers (like
  // fab_secondary used by MundialPredictionFloatingCard) miss its 56px
  // height and collide with the dock.
  const stackOffset = useLayerOffset("floating_cta");
  const dockVisible = isMobile && !cleanScreen && !liveMode;
  useEffect(() => {
    setLayerActive("floating_cta", dockVisible);
    return () => setLayerActive("floating_cta", false);
  }, [dockVisible]);

  useEffect(() => {
    if (!isMobile || cleanScreen || liveMode) return;
    void trackEvent("mobile_share_dock_shown", { nation });
  }, [isMobile, cleanScreen, liveMode, nation]);

  if (!isMobile || cleanScreen || liveMode) return null;

  if (typeof window !== "undefined" && window.location.pathname === "/") return null;

  const onShare = () => {
    void trackEvent("mobile_share_dock_share_clicked", {});
    openShareSheet();
  };
  const onInvite = () => {
    void trackEvent("mobile_share_dock_invite_clicked", {});
    openShareSheet();
  };
  const onNation = () => {
    void trackEvent("mobile_share_dock_nation_clicked", { nation });
  };

  return (
    <nav
      aria-label="Quick share"
      className="fixed inset-x-0 z-[60] lg:hidden"
      style={{
        bottom: `calc(env(safe-area-inset-bottom) + ${stackOffset || 64}px)`,
        paddingLeft: "max(env(safe-area-inset-left), 0px)",
        paddingRight: "max(env(safe-area-inset-right), 0px)",
      }}
    >
      <div className="mx-2 mb-2 grid grid-cols-3 gap-1 rounded-2xl border border-border/60 bg-background/85 p-1 shadow-lg backdrop-blur-md">
        <button
          type="button"
          onClick={onShare}
          className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl bg-primary px-2 text-[12px] font-black text-primary-foreground transition active:scale-95"
        >
          <Share2 className="h-4 w-4" />
          <span>Compartir</span>
        </button>
        <button
          type="button"
          onClick={onInvite}
          className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl bg-secondary px-2 text-[12px] font-black text-foreground transition active:scale-95"
        >
          <UserPlus className="h-4 w-4" />
          <span>Invitar</span>
        </button>
        <Link
          to={nation ? "/nation-wars" : "/onboarding"}
          onClick={onNation}
          className="flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl bg-accent px-2 text-[12px] font-black text-accent-foreground transition active:scale-95"
        >
          <Flag className="h-4 w-4" />
          <span>{nation ?? "Mi Nación"}</span>
        </Link>
      </div>
    </nav>
  );
}